import { ApiErrorCode, zApiError } from '@ultrasnip/contracts';

/** Error thrown for every failed API call; `code` is the stable machine-readable reason. */
export class ApiRequestError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: ApiErrorCode | 'network_error',
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  public get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Requirements listed by a `step_up_required` error (e.g. ['password', 'totp']). */
  public get stepUpRequirements(): string[] {
    const requires = (this.details as { requires?: unknown } | undefined)?.requires;
    return Array.isArray(requires) ? requires.filter((r): r is string => typeof r === 'string') : [];
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

/**
 * Whether an administrator deactivated the signed-in account. The API then refuses every request
 * with 403 `account_deactivated` except `GET /me` (which says `user.deactivated`) and signing out.
 * Once the client knows, it stops calling the API: every request other than `GET /me` is held
 * (never sent, never settled, released only by its abort signal), so nothing polls behind the
 * "Account deactivated" layer and no screen raises an error toast. It is cleared by a page load
 * only: the layer reloads the app when the account is active again, and signs out with a full
 * navigation.
 */
export class AccountGate {
  private deactivated = false;
  private readonly listeners = new Set<() => void>();

  public get isDeactivated(): boolean {
    return this.deactivated;
  }

  /** Records that the account is deactivated and tells the listeners (once). */
  public markDeactivated(): void {
    if (this.deactivated) return;
    this.deactivated = true;
    for (const listener of [...this.listeners]) listener();
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

/** One gate per tab, shared by the API client and the live channel. */
export const accountGate = new AccountGate();

/** `GET /me`: the one call a deactivated account still makes (the layer's re-check). */
function isMeRead(method: string, path: string): boolean {
  return method === 'GET' && path === '/me';
}

/** A request that never settles, except by rejecting with `AbortError` when its signal aborts. */
function held<T>(signal: AbortSignal | undefined): Promise<T> {
  return new Promise<T>((_resolve, reject) => {
    const abort = () => reject(new DOMException('The request was aborted', 'AbortError'));
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
  });
}

/** Thin, typed JSON client for the same-origin Ultra-Snip API. */
export class ApiClient {
  public constructor(
    private readonly baseUrl = '/api',
    private readonly gate: AccountGate = accountGate,
  ) {}

  public get<T>(path: string, options: { query?: Query; signal?: AbortSignal } = {}): Promise<T> {
    return this.request<T>('GET', path, undefined, options);
  }

  public post<T>(path: string, body?: unknown, options: { signal?: AbortSignal } = {}): Promise<T> {
    return this.request<T>('POST', path, body, options);
  }

  public patch<T>(path: string, body?: unknown, options: { signal?: AbortSignal } = {}): Promise<T> {
    return this.request<T>('PATCH', path, body, options);
  }

  public put<T>(path: string, body?: unknown, options: { signal?: AbortSignal } = {}): Promise<T> {
    return this.request<T>('PUT', path, body, options);
  }

  public delete<T>(path: string, body?: unknown, options: { signal?: AbortSignal } = {}): Promise<T> {
    return this.request<T>('DELETE', path, body, options);
  }

  private async request<T>(
    method: string,
    path: string,
    body: unknown,
    options: { query?: Query; signal?: AbortSignal | undefined },
  ): Promise<T> {
    const meRead = isMeRead(method, path);
    if (this.gate.isDeactivated && !meRead) return held<T>(options.signal);
    const url = new URL(`${this.baseUrl}${path}`, window.location.origin);
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers:
          body === undefined
            ? { accept: 'application/json' }
            : { 'content-type': 'application/json', accept: 'application/json' },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        ...(options.signal ? { signal: options.signal } : {}),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new ApiRequestError(
        0,
        'network_error',
        "Can't reach Ultra-Snip. Check your connection and try again.",
      );
    }

    if (response.status === 204) return undefined as T;
    const text = await response.text();
    let data: unknown;
    try {
      data = text ? JSON.parse(text) : undefined;
    } catch {
      data = undefined;
    }

    if (!response.ok) {
      const parsed = zApiError.safeParse(data);
      if (parsed.success) {
        const { code, message, details } = parsed.data.error;
        // Deactivated while the app was open: the layer takes over; this call never settles.
        if (response.status === 403 && code === ApiErrorCode.AccountDeactivated) {
          this.gate.markDeactivated();
          return held<T>(options.signal);
        }
        throw new ApiRequestError(response.status, code as ApiErrorCode, message, details);
      }
      throw new ApiRequestError(
        response.status,
        'internal_error',
        'Something went wrong on our side. Try again.',
      );
    }
    if (meRead && (data as { user?: { deactivated?: unknown } } | undefined)?.user?.deactivated === true) {
      this.gate.markDeactivated();
    }
    return data as T;
  }
}

export const api = new ApiClient();
